import { useState, useMemo } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { useAdminData } from "@/hooks/useAdminData";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Search, MoreVertical, Ban, CheckCircle, CreditCard, Loader2, Plus, Store, UserPlus, Pencil, AlertCircle, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { AdminRestaurant } from "@/hooks/useAdminData";

// Validação de email com zod
const emailSchema = z.string().email("Email inválido");

// Função para calcular força da senha
const getPasswordStrength = (password: string): { score: number; label: string; color: string } => {
  if (!password) return { score: 0, label: "", color: "" };
  
  let score = 0;
  
  // Comprimento
  if (password.length >= 6) score += 20;
  if (password.length >= 8) score += 10;
  if (password.length >= 12) score += 10;
  
  // Letras minúsculas
  if (/[a-z]/.test(password)) score += 15;
  
  // Letras maiúsculas
  if (/[A-Z]/.test(password)) score += 15;
  
  // Números
  if (/[0-9]/.test(password)) score += 15;
  
  // Caracteres especiais
  if (/[^a-zA-Z0-9]/.test(password)) score += 15;
  
  if (score < 30) return { score, label: "Muito fraca", color: "bg-destructive" };
  if (score < 50) return { score, label: "Fraca", color: "bg-orange-500" };
  if (score < 70) return { score, label: "Média", color: "bg-yellow-500" };
  if (score < 90) return { score, label: "Forte", color: "bg-green-500" };
  return { score: 100, label: "Muito forte", color: "bg-green-600" };
};

const statusColors: Record<string, string> = {
  active: "bg-green-100 text-green-700",
  trial: "bg-blue-100 text-blue-700",
  suspended: "bg-destructive/10 text-destructive",
  cancelled: "bg-gray-100 text-gray-700",
};

const statusLabels: Record<string, string> = {
  active: "Ativo",
  trial: "Trial",
  suspended: "Suspenso",
  cancelled: "Cancelado",
};

const planLabels: Record<string, string> = {
  trial: "Trial",
  basic: "Básico",
  pro: "Profissional",
  enterprise: "Enterprise",
};

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

function activityColor(iso: string): string {
  const days = daysSince(iso);
  if (days <= 3) return "text-green-600";
  if (days <= 14) return "text-amber-600";
  return "text-red-600";
}

function relativeLabel(iso: string): string {
  const days = daysSince(iso);
  if (days <= 0) return "hoje";
  if (days === 1) return "ontem";
  if (days < 30) return `há ${days} dias`;
  const months = Math.floor(days / 30);
  return months === 1 ? "há 1 mês" : `há ${months} meses`;
}

export default function AdminRestaurantesPage() {
  const { user } = useAuth();
  const { restaurants, isLoading, suspendRestaurant, activateRestaurant, createSubscription, registerPayment, createRestaurant, linkOwnerToRestaurant, fetchAvailableUsers, updateRestaurant } = useAdminData();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedRestaurant, setSelectedRestaurant] = useState<AdminRestaurant | null>(null);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<{ id: string; full_name: string | null; phone: string | null }[]>([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [paymentData, setPaymentData] = useState({
    amount: "",
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    method: "pix",
  });
  const [subscriptionData, setSubscriptionData] = useState({
    plan: "basic",
    amount: "99.90",
  });
  const [newRestaurant, setNewRestaurant] = useState({
    name: "",
    slug: "",
    phone: "",
    address: "",
    plan: "trial",
    amount: "0",
    createOwner: false,
    ownerEmail: "",
    ownerPassword: "",
    ownerName: "",
    ownerPhone: "",
    sendWelcomeEmail: true,
  });
  const [editData, setEditData] = useState({
    name: "",
    slug: "",
    phone: "",
    address: "",
  });

  const filteredRestaurants = restaurants.filter((r) => {
    const matchesSearch =
      r.name.toLowerCase().includes(search.toLowerCase()) ||
      r.owner?.full_name?.toLowerCase().includes(search.toLowerCase());
    
    if (statusFilter === "all") return matchesSearch;
    
    const status = r.subscription?.status || "active";
    return matchesSearch && status === statusFilter;
  });

  const handleSuspend = async () => {
    if (!selectedRestaurant) return;
    await suspendRestaurant(selectedRestaurant.id, suspendReason);
    setSuspendDialogOpen(false);
    setSuspendReason("");
    setSelectedRestaurant(null);
  };

  const handleActivate = async (restaurant: AdminRestaurant) => {
    await activateRestaurant(restaurant.id);
  };

  const handleRegisterPayment = async () => {
    if (!selectedRestaurant?.subscription || !user) return;
    await registerPayment(
      selectedRestaurant.subscription.id,
      selectedRestaurant.id,
      parseFloat(paymentData.amount),
      paymentData.month,
      paymentData.year,
      paymentData.method,
      user.id
    );
    setPaymentDialogOpen(false);
    setPaymentData({
      amount: "",
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      method: "pix",
    });
  };

  const handleCreateSubscription = async () => {
    if (!selectedRestaurant) return;
    await createSubscription(
      selectedRestaurant.id,
      subscriptionData.plan,
      parseFloat(subscriptionData.amount)
    );
    setSubscriptionDialogOpen(false);
  };

  // Validações de email e senha
  const emailValidation = useMemo(() => {
    if (!newRestaurant.ownerEmail) return { valid: true, error: "" };
    const result = emailSchema.safeParse(newRestaurant.ownerEmail);
    return { valid: result.success, error: result.success ? "" : "Email inválido" };
  }, [newRestaurant.ownerEmail]);

  const passwordStrength = useMemo(() => {
    return getPasswordStrength(newRestaurant.ownerPassword);
  }, [newRestaurant.ownerPassword]);

  const isPasswordValid = newRestaurant.ownerPassword.length >= 6;

  const handleCreateRestaurant = async () => {
    if (!newRestaurant.name || !newRestaurant.slug) return;
    
    if (newRestaurant.createOwner) {
      if (!newRestaurant.ownerEmail || !newRestaurant.ownerPassword) {
        toast.error('Email e senha são obrigatórios para criar o usuário');
        return;
      }
      
      if (!emailValidation.valid) {
        toast.error('Por favor, insira um email válido');
        return;
      }
      
      if (!isPasswordValid) {
        toast.error('A senha deve ter pelo menos 6 caracteres');
        return;
      }
    }
    
    setIsCreating(true);
    const result = await createRestaurant({
      name: newRestaurant.name,
      slug: newRestaurant.slug.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
      phone: newRestaurant.phone || undefined,
      address: newRestaurant.address || undefined,
      plan: newRestaurant.plan,
      amount: parseFloat(newRestaurant.amount) || 0,
      createOwner: newRestaurant.createOwner,
      ownerEmail: newRestaurant.ownerEmail || undefined,
      ownerPassword: newRestaurant.ownerPassword || undefined,
      ownerName: newRestaurant.ownerName || undefined,
      ownerPhone: newRestaurant.ownerPhone || undefined,
      sendWelcomeEmail: newRestaurant.sendWelcomeEmail,
    });
    setIsCreating(false);
    
    if (result) {
      setCreateDialogOpen(false);
      setNewRestaurant({
        name: "",
        slug: "",
        phone: "",
        address: "",
        plan: "trial",
        amount: "0",
        createOwner: false,
        ownerEmail: "",
        ownerPassword: "",
        ownerName: "",
        ownerPhone: "",
        sendWelcomeEmail: true,
      });
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  };

  const handleOpenOwnerDialog = async (restaurant: AdminRestaurant) => {
    setSelectedRestaurant(restaurant);
    setSelectedUserId("");
    const users = await fetchAvailableUsers();
    setAvailableUsers(users);
    setOwnerDialogOpen(true);
  };

  const handleLinkOwner = async () => {
    if (!selectedRestaurant || !selectedUserId) return;
    setIsCreating(true);
    await linkOwnerToRestaurant(selectedRestaurant.id, selectedUserId);
    setIsCreating(false);
    setOwnerDialogOpen(false);
    setSelectedUserId("");
  };

  const handleOpenEditDialog = (restaurant: AdminRestaurant) => {
    setSelectedRestaurant(restaurant);
    setEditData({
      name: restaurant.name,
      slug: restaurant.slug,
      phone: restaurant.phone || "",
      address: restaurant.address || "",
    });
    setEditDialogOpen(true);
  };

  const handleUpdateRestaurant = async () => {
    if (!selectedRestaurant || !editData.name || !editData.slug) return;
    setIsCreating(true);
    const success = await updateRestaurant(selectedRestaurant.id, {
      name: editData.name,
      slug: editData.slug.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
      phone: editData.phone || undefined,
      address: editData.address || undefined,
    });
    setIsCreating(false);
    if (success) {
      setEditDialogOpen(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Restaurantes</h1>
          <p className="text-muted-foreground">Gerencie todos os restaurantes da plataforma</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Store className="mr-2 h-4 w-4" />
          Novo Restaurante
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Lista de Restaurantes</CardTitle>
              <CardDescription>
                {filteredRestaurants.length} de {restaurants.length} restaurantes
              </CardDescription>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="flex gap-1">
                <Button
                  variant={statusFilter === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter("all")}
                >
                  Todos
                </Button>
                <Button
                  variant={statusFilter === "active" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter("active")}
                  className={statusFilter === "active" ? "" : "text-green-600 border-green-200 hover:bg-green-50"}
                >
                  <CheckCircle className="mr-1 h-3 w-3" />
                  Ativos
                </Button>
                <Button
                  variant={statusFilter === "trial" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter("trial")}
                  className={statusFilter === "trial" ? "" : "text-blue-600 border-blue-200 hover:bg-blue-50"}
                >
                  Trial
                </Button>
                <Button
                  variant={statusFilter === "suspended" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter("suspended")}
                  className={statusFilter === "suspended" ? "" : "text-destructive border-destructive/20 hover:bg-destructive/10"}
                >
                  <Ban className="mr-1 h-3 w-3" />
                  Suspensos
                </Button>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar restaurante..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Restaurante</TableHead>
                <TableHead>Proprietário</TableHead>
                <TableHead>Plano</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Último acesso</TableHead>
                <TableHead>Cadastro</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRestaurants.map((restaurant) => (
                <TableRow key={restaurant.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{restaurant.name}</p>
                      <p className="text-xs text-muted-foreground">{restaurant.slug}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p>{restaurant.owner?.full_name || "-"}</p>
                      <p className="text-xs text-muted-foreground">{restaurant.owner?.phone || "-"}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {restaurant.subscription ? (
                      <div>
                        <p className="font-medium">{planLabels[restaurant.subscription.plan] || restaurant.subscription.plan}</p>
                        {restaurant.subscription.plan !== 'trial' && (
                          <p className="text-xs text-muted-foreground">
                            R$ {Number(restaurant.subscription.amount).toFixed(2)}/mês
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Sem assinatura</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="secondary"
                      className={statusColors[restaurant.subscription?.status || "active"]}
                    >
                      {statusLabels[restaurant.subscription?.status || "active"]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {restaurant.last_login ? (
                      <div>
                        <p className={`font-medium ${activityColor(restaurant.last_login)}`}>
                          {format(new Date(restaurant.last_login), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {relativeLabel(restaurant.last_login)}
                          {restaurant.last_order
                            ? ` · último pedido ${format(new Date(restaurant.last_order), "dd/MM/yyyy", { locale: ptBR })}`
                            : " · sem pedidos"}
                        </p>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Nunca acessou</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {format(new Date(restaurant.created_at), "dd/MM/yyyy", { locale: ptBR })}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenEditDialog(restaurant)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Editar
                        </DropdownMenuItem>
                        {restaurant.subscription?.status === "suspended" ? (
                          <DropdownMenuItem onClick={() => handleActivate(restaurant)}>
                            <CheckCircle className="mr-2 h-4 w-4" />
                            Reativar
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedRestaurant(restaurant);
                              setSuspendDialogOpen(true);
                            }}
                          >
                            <Ban className="mr-2 h-4 w-4" />
                            Suspender
                          </DropdownMenuItem>
                        )}
                        {restaurant.subscription ? (
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedRestaurant(restaurant);
                              setPaymentData({
                                ...paymentData,
                                amount: restaurant.subscription?.amount?.toString() || "",
                              });
                              setPaymentDialogOpen(true);
                            }}
                          >
                            <CreditCard className="mr-2 h-4 w-4" />
                            Registrar Pagamento
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedRestaurant(restaurant);
                              setSubscriptionDialogOpen(true);
                            }}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Criar Assinatura
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleOpenOwnerDialog(restaurant)}>
                          <UserPlus className="mr-2 h-4 w-4" />
                          Vincular Proprietário
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspender Restaurante</DialogTitle>
            <DialogDescription>
              Informe o motivo da suspensão de {selectedRestaurant?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Motivo da suspensão</Label>
              <Textarea
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Ex: Falta de pagamento - 2 meses em atraso"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleSuspend}>
              Suspender
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Pagamento</DialogTitle>
            <DialogDescription>
              Registrar pagamento para {selectedRestaurant?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Valor</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Mês referência</Label>
                <Select
                  value={paymentData.month.toString()}
                  onValueChange={(v) => setPaymentData({ ...paymentData, month: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>
                        {format(new Date(2024, i, 1), "MMMM", { locale: ptBR })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ano</Label>
                <Select
                  value={paymentData.year.toString()}
                  onValueChange={(v) => setPaymentData({ ...paymentData, year: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Forma de pagamento</Label>
              <Select
                value={paymentData.method}
                onValueChange={(v) => setPaymentData({ ...paymentData, method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pix">PIX</SelectItem>
                  <SelectItem value="boleto">Boleto</SelectItem>
                  <SelectItem value="cartao">Cartão</SelectItem>
                  <SelectItem value="transferencia">Transferência</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleRegisterPayment}>Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Subscription Dialog */}
      <Dialog open={subscriptionDialogOpen} onOpenChange={setSubscriptionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar Assinatura</DialogTitle>
            <DialogDescription>
              Criar assinatura para {selectedRestaurant?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Plano</Label>
              <Select
                value={subscriptionData.plan}
                onValueChange={(v) => setSubscriptionData({ ...subscriptionData, plan: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial (14 dias)</SelectItem>
                  <SelectItem value="basic">Básico</SelectItem>
                  <SelectItem value="pro">Profissional</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Valor mensal</Label>
              <Input
                type="number"
                step="0.01"
                value={subscriptionData.amount}
                onChange={(e) => setSubscriptionData({ ...subscriptionData, amount: e.target.value })}
                placeholder="99.90"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubscriptionDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateSubscription}>Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Restaurant Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Restaurante</DialogTitle>
            <DialogDescription>
              Cadastre um novo restaurante na plataforma
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do restaurante *</Label>
              <Input
                value={newRestaurant.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setNewRestaurant({
                    ...newRestaurant,
                    name,
                    slug: generateSlug(name),
                  });
                }}
                placeholder="Ex: Pizzaria do João"
              />
            </div>
            <div>
              <Label>Slug (URL) *</Label>
              <Input
                value={newRestaurant.slug}
                onChange={(e) => setNewRestaurant({ ...newRestaurant, slug: e.target.value })}
                placeholder="pizzaria-do-joao"
              />
              <p className="text-xs text-muted-foreground mt-1">
                URL: /menu/{newRestaurant.slug || "slug"}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Telefone</Label>
                <Input
                  value={newRestaurant.phone}
                  onChange={(e) => setNewRestaurant({ ...newRestaurant, phone: e.target.value })}
                  placeholder="(11) 99999-9999"
                />
              </div>
              <div>
                <Label>Plano inicial</Label>
                <Select
                  value={newRestaurant.plan}
                  onValueChange={(v) => setNewRestaurant({ ...newRestaurant, plan: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial (14 dias)</SelectItem>
                    <SelectItem value="basic">Básico</SelectItem>
                    <SelectItem value="pro">Profissional</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Endereço</Label>
              <Input
                value={newRestaurant.address}
                onChange={(e) => setNewRestaurant({ ...newRestaurant, address: e.target.value })}
                placeholder="Rua das Flores, 123"
              />
            </div>
            {newRestaurant.plan !== "trial" && (
              <div>
                <Label>Valor mensal (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newRestaurant.amount}
                  onChange={(e) => setNewRestaurant({ ...newRestaurant, amount: e.target.value })}
                  placeholder="99.90"
                />
              </div>
            )}
            
            {/* Seção de criação de usuário */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center space-x-2 mb-4">
                <input
                  type="checkbox"
                  id="createOwner"
                  checked={newRestaurant.createOwner}
                  onChange={(e) => setNewRestaurant({ ...newRestaurant, createOwner: e.target.checked })}
                  className="h-4 w-4 rounded border-border"
                />
                <Label htmlFor="createOwner" className="text-sm font-medium cursor-pointer">
                  Criar usuário administrador para este restaurante
                </Label>
              </div>
              
              {newRestaurant.createOwner && (
                <div className="space-y-4 pl-6 border-l-2 border-primary/20">
                  <div>
                    <Label>Nome do proprietário</Label>
                    <Input
                      value={newRestaurant.ownerName}
                      onChange={(e) => setNewRestaurant({ ...newRestaurant, ownerName: e.target.value })}
                      placeholder="João Silva"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <div className="relative">
                      <Input
                        type="email"
                        value={newRestaurant.ownerEmail}
                        onChange={(e) => setNewRestaurant({ ...newRestaurant, ownerEmail: e.target.value })}
                        placeholder="joao@email.com"
                        className={newRestaurant.ownerEmail && !emailValidation.valid ? "border-destructive pr-10" : newRestaurant.ownerEmail && emailValidation.valid ? "border-green-500 pr-10" : ""}
                      />
                      {newRestaurant.ownerEmail && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {emailValidation.valid ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-destructive" />
                          )}
                        </div>
                      )}
                    </div>
                    {newRestaurant.ownerEmail && !emailValidation.valid && (
                      <p className="text-xs text-destructive mt-1">{emailValidation.error}</p>
                    )}
                  </div>
                  <div>
                    <Label>Senha *</Label>
                    <Input
                      type="password"
                      value={newRestaurant.ownerPassword}
                      onChange={(e) => setNewRestaurant({ ...newRestaurant, ownerPassword: e.target.value })}
                      placeholder="Mínimo 6 caracteres"
                      className={newRestaurant.ownerPassword && !isPasswordValid ? "border-destructive" : ""}
                    />
                    {newRestaurant.ownerPassword && (
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Força da senha:</span>
                          <span className={`font-medium ${passwordStrength.score < 50 ? "text-destructive" : passwordStrength.score < 70 ? "text-yellow-600" : "text-green-600"}`}>
                            {passwordStrength.label}
                          </span>
                        </div>
                        <Progress value={passwordStrength.score} className="h-1.5" />
                        <ul className="text-xs text-muted-foreground space-y-0.5 mt-2">
                          <li className={newRestaurant.ownerPassword.length >= 6 ? "text-green-600" : ""}>
                            {newRestaurant.ownerPassword.length >= 6 ? "✓" : "○"} Mínimo 6 caracteres
                          </li>
                          <li className={/[A-Z]/.test(newRestaurant.ownerPassword) ? "text-green-600" : ""}>
                            {/[A-Z]/.test(newRestaurant.ownerPassword) ? "✓" : "○"} Letra maiúscula
                          </li>
                          <li className={/[0-9]/.test(newRestaurant.ownerPassword) ? "text-green-600" : ""}>
                            {/[0-9]/.test(newRestaurant.ownerPassword) ? "✓" : "○"} Número
                          </li>
                          <li className={/[^a-zA-Z0-9]/.test(newRestaurant.ownerPassword) ? "text-green-600" : ""}>
                            {/[^a-zA-Z0-9]/.test(newRestaurant.ownerPassword) ? "✓" : "○"} Caractere especial
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>Telefone do proprietário</Label>
                    <Input
                      value={newRestaurant.ownerPhone}
                      onChange={(e) => setNewRestaurant({ ...newRestaurant, ownerPhone: e.target.value })}
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                  
                  {/* Checkbox enviar email de boas-vindas */}
                  <div className="flex items-center space-x-2 pt-2 border-t">
                    <input
                      type="checkbox"
                      id="sendWelcomeEmail"
                      checked={newRestaurant.sendWelcomeEmail}
                      onChange={(e) => setNewRestaurant({ ...newRestaurant, sendWelcomeEmail: e.target.checked })}
                      className="h-4 w-4 rounded border-border"
                    />
                    <Label htmlFor="sendWelcomeEmail" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                      <span>📧</span> Enviar email de boas-vindas
                    </Label>
                  </div>
                  {newRestaurant.sendWelcomeEmail && (
                    <p className="text-xs text-muted-foreground pl-6">
                      O novo usuário receberá um email com instruções de acesso, dados de login e como instalar o app no celular.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateRestaurant} disabled={isCreating || !newRestaurant.name || !newRestaurant.slug}>
              {isCreating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Store className="h-4 w-4 mr-2" />}
              Criar Restaurante
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Owner Dialog */}
      <Dialog open={ownerDialogOpen} onOpenChange={setOwnerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vincular Proprietário</DialogTitle>
            <DialogDescription>
              Vincular um usuário como proprietário de {selectedRestaurant?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Selecionar usuário</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Escolha um usuário..." />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.full_name || "Sem nome"} {u.phone ? `(${u.phone})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedRestaurant?.owner && (
              <p className="text-sm text-muted-foreground">
                Proprietário atual: {selectedRestaurant.owner.full_name || "Sem nome"}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOwnerDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleLinkOwner} disabled={isCreating || !selectedUserId}>
              {isCreating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Vincular
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Restaurant Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Restaurante</DialogTitle>
            <DialogDescription>
              Edite os dados de {selectedRestaurant?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do restaurante *</Label>
              <Input
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                placeholder="Ex: Pizzaria do João"
              />
            </div>
            <div>
              <Label>Slug (URL) *</Label>
              <Input
                value={editData.slug}
                onChange={(e) => setEditData({ ...editData, slug: e.target.value })}
                placeholder="pizzaria-do-joao"
              />
              <p className="text-xs text-muted-foreground mt-1">
                URL: /menu/{editData.slug || "slug"}
              </p>
            </div>
            <div>
              <Label>Telefone</Label>
              <Input
                value={editData.phone}
                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                placeholder="(11) 99999-9999"
              />
            </div>
            <div>
              <Label>Endereço</Label>
              <Input
                value={editData.address}
                onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                placeholder="Rua das Flores, 123"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateRestaurant} disabled={isCreating || !editData.name || !editData.slug}>
              {isCreating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Pencil className="h-4 w-4 mr-2" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
